<template>
  <b-modal ref="modal" size="xl" hide-footer scrollable body-class="p-0" @shown="focusSearch">
    <template #modal-title>
      <span class="mfg-guide-title">Manufacturing guide</span>
      <span class="text-secondary d-block" style="font-size:12px;font-weight:400;">Plain-language help, with shortcuts to the right screen.</span>
    </template>

    <div class="mfg-guide">
      <!-- Sidebar: search + sections -->
      <div class="mfg-guide-nav">
        <div class="input-group mb-3">
          <div class="input-group-prepend"><span class="input-group-text bg-white"><i class="bi bi-search text-secondary"></i></span></div>
          <input ref="search" v-model="query" type="search" class="form-control" placeholder="Search the guide… e.g. bags, recall, cost" />
        </div>
        <div v-if="query && !results.length" class="text-secondary" style="font-size:13px;">Nothing matches “{{ query }}”. Try a simpler word like <a href="#" @click.prevent="query = 'cost'">cost</a> or <a href="#" @click.prevent="query = 'recipe'">recipe</a>.</div>
        <a v-for="s in results" :key="s.id" href="#" class="mfg-guide-link" :class="{ active: s.id === activeId }" @click.prevent="open(s.id)">
          <i class="bi mr-2" :class="s.icon"></i>{{ s.title }}
        </a>
      </div>

      <!-- Content -->
      <div class="mfg-guide-body" ref="body">
        <div v-if="section">
          <h5 class="mfg-guide-h"><i class="bi mr-2 text-primary" :class="section.icon"></i>{{ section.title }}</h5>

          <div v-for="(b, i) in section.blocks" :key="i" class="mb-3">
            <p v-if="b.type === 'p'" class="mb-0" v-html="mark(b.text)"></p>

            <template v-else-if="b.type === 'steps' || b.type === 'list'">
              <div v-if="b.title" class="mfg-guide-sub">{{ b.title }}</div>
              <component :is="b.type === 'steps' ? 'ol' : 'ul'" class="mb-0 pl-3">
                <li v-for="(it, j) in b.items" :key="j" class="mb-1" v-html="mark(it)"></li>
              </component>
            </template>

            <div v-else-if="b.type === 'example'" class="mfg-guide-box">
              <div class="mfg-guide-sub mb-1"><i class="bi bi-lightbulb mr-1 text-primary"></i>{{ b.title }}</div>
              <div v-for="(l, j) in b.lines" :key="j" style="font-size:13px;" v-html="mark(l)"></div>
            </div>

            <div v-else-if="b.type === 'tip'" class="mfg-guide-note"><i class="bi bi-info-circle mr-2 text-secondary"></i><span v-html="mark(b.text)"></span></div>
            <div v-else-if="b.type === 'warn'" class="mfg-guide-note mfg-guide-note--warn"><i class="bi bi-exclamation-circle mr-2 text-danger"></i><span v-html="mark(b.text)"></span></div>

            <div v-else-if="b.type === 'faq'">
              <div v-for="(f, j) in b.items" :key="j" class="mfg-guide-faq">
                <div class="font-weight-bold text-dark" style="font-size:13px;" v-html="mark(f.q)"></div>
                <div class="text-secondary" style="font-size:13px;" v-html="mark(f.a)"></div>
              </div>
            </div>
          </div>

          <div v-if="visibleActions.length" class="mfg-guide-actions">
            <span class="text-secondary mr-2" style="font-size:12px;">Take me there:</span>
            <button v-for="(a, i) in visibleActions" :key="i" class="btn btn-sm" :class="i === 0 ? 'btn-primary' : 'btn-outline-secondary'" @click="go(a.action)">
              {{ a.label }} <i class="bi bi-arrow-right-short"></i>
            </button>
          </div>

          <div class="d-flex justify-content-between mt-4 pt-3" style="border-top:1px solid #f1f5f9;">
            <a v-if="prev" href="#" class="text-dark" style="font-size:13px;" @click.prevent="open(prev.id)"><i class="bi bi-arrow-left mr-1"></i>{{ prev.title }}</a><span v-else></span>
            <a v-if="next" href="#" class="text-dark" style="font-size:13px;font-weight:600;" @click.prevent="open(next.id)">{{ next.title }} <i class="bi bi-arrow-right ml-1"></i></a>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import sections from './guideContent';

const textOf = (s) => [s.title, s.keywords, ...s.blocks.flatMap((b) => [
  b.text, b.title, ...(b.items || []).map((it) => (typeof it === 'string' ? it : `${it.q} ${it.a}`)), ...(b.lines || []),
])].filter(Boolean).join(' ').toLowerCase();

export default {
  name: 'ManufacturingGuide',
  props: {
    // Actions not available right now (e.g. sampleData on production).
    hiddenActions: { type: Array, default: () => [] },
  },
  data() {
    return { sections, query: '', activeId: sections[0].id };
  },
  computed: {
    results() {
      const words = this.query.toLowerCase().trim().split(/\s+/).filter(Boolean);
      if (!words.length) return this.sections;
      return this.sections.filter((s) => { const t = textOf(s); return words.every((w) => t.includes(w)); });
    },
    visibleActions() {
      return ((this.section && this.section.actions) || []).filter((a) => !this.hiddenActions.includes(a.action));
    },
    section() {
      return this.results.find((s) => s.id === this.activeId) || this.results[0] || null;
    },
    position() { return this.sections.findIndex((s) => s.id === (this.section && this.section.id)); },
    prev() { return this.position > 0 ? this.sections[this.position - 1] : null; },
    next() { return this.position >= 0 && this.position < this.sections.length - 1 ? this.sections[this.position + 1] : null; },
  },
  watch: {
    results(list) {
      if (list.length && !list.some((s) => s.id === this.activeId)) this.activeId = list[0].id;
    },
  },
  methods: {
    /** Open the guide, optionally at a section id. */
    show(sectionId = null) {
      if (sectionId) this.activeId = sectionId;
      this.$refs.modal.show();
    },
    open(id) {
      this.activeId = id;
      this.$nextTick(() => { if (this.$refs.body) this.$refs.body.scrollTop = 0; });
    },
    focusSearch() { this.$refs.search && this.$refs.search.focus(); },
    go(action) {
      this.$refs.modal.hide();
      this.$emit('action', action);
    },
    /** Escape text, then highlight search words. */
    mark(text) {
      const safe = String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const words = this.query.trim().split(/\s+/).filter((w) => w.length > 1);
      if (!words.length) return safe;
      const re = new RegExp(`(${words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
      return safe.replace(re, '<mark class="p-0">$1</mark>');
    },
  },
};
</script>

<style scoped>
.mfg-guide-title { color: #fd4719; font-size: 20px; font-weight: 600; letter-spacing: -0.3px; }
.mfg-guide { display: flex; min-height: 60vh; }
.mfg-guide-nav { width: 280px; flex-shrink: 0; padding: 20px; border-right: 1px solid #eef0f2; background: #f8fafc; overflow-y: auto; }
.mfg-guide-link { display: block; padding: 7px 10px; border-radius: 8px; font-size: 13px; color: #334155; text-decoration: none; }
.mfg-guide-link:hover { background: #fff; color: #0f172a; text-decoration: none; }
.mfg-guide-link.active { background: #fff; color: #0f172a; font-weight: 600; box-shadow: inset 3px 0 0 #fd4719; }
.mfg-guide-body { flex: 1; padding: 24px 28px; overflow-y: auto; font-size: 14px; color: #0f172a; line-height: 1.55; }
.mfg-guide-h { font-size: 18px; font-weight: 600; margin-bottom: 16px; }
.mfg-guide-sub { font-size: 13px; font-weight: 600; color: #0f172a; margin-bottom: 4px; }
.mfg-guide-box { background: #fff; border: 1px solid #e9ecef; border-radius: 12px; padding: 12px 16px; }
.mfg-guide-note { display: flex; align-items: flex-start; font-size: 13px; color: #334155; background: #f8fafc; border-radius: 10px; padding: 10px 14px; }
.mfg-guide-note--warn { background: #fdf2f2; }
.mfg-guide-faq { padding: 10px 0; border-top: 1px solid #f1f5f9; }
.mfg-guide-faq:first-child { border-top: 0; padding-top: 0; }
.mfg-guide-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 18px; }
@media (max-width: 767px) {
  .mfg-guide { flex-direction: column; }
  .mfg-guide-nav { width: 100%; border-right: 0; border-bottom: 1px solid #eef0f2; max-height: 38vh; }
  .mfg-guide-body { padding: 18px 16px; }
}
</style>
